import React, {FC} from 'react';
import styled from 'styled-components';

import {ChannelModel} from '../../../models/api/channelModel';
import {ConversationModel} from '../../../models/api/conversationModel';
import {MessageTypesEnum} from '../../../models/api/messageModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {NewPanelId} from '../../../store/panels/panelStateBase';
import {ComposerHeaderBanner} from './banner/composerHeaderBanner';
import {ComposerAvatar} from './composerAvatar';
import {ComposerDetails, ComposerDetailsPublicProps} from './composerDetails';

/*
 * Props.
 */

export interface ChatComposerHeaderProps extends ComposerDetailsPublicProps {
  user: TeammateModel;
  channel: ChannelModel;
  conversation: ConversationModel | null;
  hasRecentCommunicationsComposerBanner: boolean;
  panelId: NewPanelId;
  isComposerPopup: boolean;
}

/*
 * Style.
 */

const StyledHeaderDiv = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto auto;
  grid-template-areas:
    'banner'
    'scroll';

  border-bottom: 1px solid ${(p) => p.theme.message.border};
`;

const StyledBannerDiv = styled.div`
  grid-area: banner;
  padding-bottom: 10px;
`;

interface ScrollStyleProps {
  $isPopup: boolean;
}

const StyledScrollDiv = styled.div<ScrollStyleProps>`
  grid-area: scroll;
  display: grid;
  align-items: flex-start;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  grid-template-areas:
    'avatar recipients'
    '. recipients';
  overflow-x: auto;
  max-height: ${(p) => (p.$isPopup ? '127px' : 'unset')};
`;

/*
 * Component.
 */

export const ChatComposerHeader: FC<ChatComposerHeaderProps> = (props) => {
  const {channel, user, isComposerPopup} = props;

  const isSMSChannel = channel.messageType === MessageTypesEnum.SMS;
  const isTweetDMChannel = channel.messageType === MessageTypesEnum.TWEET_DM;
  return (
    <StyledHeaderDiv>
      <StyledBannerDiv>{maybeRenderComposerHeaderBanner(props)}</StyledBannerDiv>
      <StyledScrollDiv $isPopup={isComposerPopup}>
        {!isSMSChannel && !isTweetDMChannel && <ComposerAvatar author={user} />}
        <ComposerDetails {...props} isCcVisible={false} isBccVisible={false} />
      </StyledScrollDiv>
    </StyledHeaderDiv>
  );
};

/*
 * Helpers.
 */

function maybeRenderComposerHeaderBanner(props: ChatComposerHeaderProps) {
  const {message, conversation, user, hasRecentCommunicationsComposerBanner} = props;
  if (!conversation) {
    return null;
  }

  return (
    <ComposerHeaderBanner
      message={message}
      conversation={conversation}
      teammateId={user.id}
      hasRecentCommunicationsComposerBanner={hasRecentCommunicationsComposerBanner}
    />
  );
}
